// frontend/src/pages/CamDoBrainRecommendation.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import camdobrainService from '../services/camdobrainService';
import Loading from '../components/Loading';
import './CamDoBrainRecommendation.css';

const CamDoBrainRecommendation = () => {
  const navigate = useNavigate();
  const { predictionId } = useParams();

  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [feedback, setFeedback] = useState('');
  const [activeTab, setActiveTab] = useState('content');

  useEffect(() => {
    loadPrediction();
  }, [predictionId]);

  const loadPrediction = async () => {
    try {
      setLoading(true);
      const res = await camdobrainService.getPrediction(predictionId);
      setPrediction(res.data);
    } catch (err) {
      console.error('Error loading prediction:', err);
      setError('Error al cargar recomendacion');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setApproving(true);
      await camdobrainService.approvePrediction(predictionId, null, scheduleDateTime || null);
      await loadPrediction();
      setShowScheduleModal(false);
    } catch (err) {
      console.error('Error approving:', err);
      setError('Error al aprobar recomendacion');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    try {
      setApproving(true);
      await camdobrainService.rejectPrediction(predictionId, feedback);
      await loadPrediction();
    } catch (err) {
      console.error('Error rejecting:', err);
      setError('Error al rechazar recomendacion');
    } finally {
      setApproving(false);
    }
  };

  const getContentTypeIcon = (type) => {
    const icons = {
      'REEL': '🎬',
      'IMAGE': '🖼️',
      'CAROUSEL': '📸',
      'VIDEO': '🎥',
      'STORY': '📱',
    };
    return icons[type] || '📄';
  };

  const getStatusColor = (status) => {
    const colors = {
      'generated': '#fbbf24',
      'pending_review': '#f97316',
      'approved': '#10b981',
      'rejected': '#ef4444',
      'scheduled': '#3b82f6',
      'published': '#8b5cf6',
    };
    return colors[status] || '#94a3b8';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'generated': 'Generado',
      'pending_review': 'Por revisar',
      'approved': 'Aprobado',
      'rejected': 'Rechazado',
      'scheduled': 'Programado',
      'published': 'Publicado',
    };
    return labels[status] || status;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return <Loading />;
  }

  if (error || !prediction) {
    return (
      <div className="recommendation-page">
        <div className="error-state">
          <p>{error || 'Recomendacion no encontrada'}</p>
          <button onClick={() => navigate('/camdobrain')}>
            Volver a CamDoBrain
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="recommendation-page">
      {/* Header */}
      <div className="recommendation-header">
        <button className="back-btn" onClick={() => navigate('/camdobrain')}>
          ← Volver
        </button>
        <div className="header-content">
          <h1>
            <span className="type-icon">{getContentTypeIcon(prediction.content_type)}</span>
            Recomendacion de {prediction.content_type}
          </h1>
          <span
            className="status-badge"
            style={{ backgroundColor: getStatusColor(prediction.status) }}
          >
            {getStatusLabel(prediction.status)}
          </span>
        </div>
      </div>

      {/* Main Grid */}
      <div className="recommendation-grid">
        {/* Left Column - Content */}
        <div className="recommendation-content">
          {/* Tabs */}
          <div className="content-tabs">
            <button
              className={`tab ${activeTab === 'content' ? 'active' : ''}`}
              onClick={() => setActiveTab('content')}
            >
              📝 Contenido
            </button>
            <button
              className={`tab ${activeTab === 'visual' ? 'active' : ''}`}
              onClick={() => setActiveTab('visual')}
            >
              🎨 Pauta Visual
            </button>
            <button
              className={`tab ${activeTab === 'reasoning' ? 'active' : ''}`}
              onClick={() => setActiveTab('reasoning')}
            >
              🧠 Razonamiento
            </button>
          </div>

          {/* Content Tab */}
          {activeTab === 'content' && (
            <div className="tab-content">
              {/* Creative Idea */}
              <div className="content-section">
                <h3>💡 Idea Creativa</h3>
                <div className="idea-card">
                  <h4>{prediction.creative_idea?.idea_title || 'Sin titulo'}</h4>
                  <p>{prediction.creative_idea?.concept || prediction.creative_idea}</p>
                  {prediction.creative_idea?.hook && (
                    <div className="idea-hook">
                      <strong>Hook:</strong> {prediction.creative_idea.hook}
                    </div>
                  )}
                </div>
              </div>

              {/* Copy */}
              <div className="content-section">
                <div className="section-header">
                  <h3>✍️ Copy</h3>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(prediction.copy_text)}
                  >
                    📋 Copiar
                  </button>
                </div>
                <div className="copy-card">
                  {prediction.hook_line && (
                    <p className="hook-line">{prediction.hook_line}</p>
                  )}
                  <p className="copy-text">{prediction.copy_text}</p>
                  {prediction.call_to_action && (
                    <p className="cta-line">{prediction.call_to_action}</p>
                  )}
                </div>

                {prediction.alternative_hooks && prediction.alternative_hooks.length > 0 && (
                  <div className="alternatives">
                    <h4>Hooks alternativos:</h4>
                    <ul>
                      {prediction.alternative_hooks.map((hook, i) => (
                        <li key={i}>{hook}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Hashtags */}
              <div className="content-section">
                <div className="section-header">
                  <h3>#️⃣ Hashtags</h3>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard((prediction.hashtags || []).join(' '))}
                  >
                    📋 Copiar
                  </button>
                </div>
                <div className="hashtags-container">
                  {(prediction.hashtags || []).map((tag, i) => (
                    <span key={i} className="hashtag">{tag}</span>
                  ))}
                </div>
                {prediction.hashtag_strategy && typeof prediction.hashtag_strategy === 'object' && (
                  <div className="hashtag-strategy">
                    {prediction.hashtag_strategy.high_reach && prediction.hashtag_strategy.high_reach.length > 0 && (
                      <div className="strategy-group">
                        <strong>Alto alcance:</strong> {prediction.hashtag_strategy.high_reach.join(', ')}
                      </div>
                    )}
                    {prediction.hashtag_strategy.medium_engagement && prediction.hashtag_strategy.medium_engagement.length > 0 && (
                      <div className="strategy-group">
                        <strong>Engagement medio:</strong> {prediction.hashtag_strategy.medium_engagement.join(', ')}
                      </div>
                    )}
                    {prediction.hashtag_strategy.niche_targeted && prediction.hashtag_strategy.niche_targeted.length > 0 && (
                      <div className="strategy-group">
                        <strong>Nicho:</strong> {prediction.hashtag_strategy.niche_targeted.join(', ')}
                      </div>
                    )}
                    {prediction.hashtag_strategy.brand && prediction.hashtag_strategy.brand.length > 0 && (
                      <div className="strategy-group">
                        <strong>Marca:</strong> {prediction.hashtag_strategy.brand.join(', ')}
                      </div>
                    )}
                  </div>
                )}
                {prediction.hashtag_strategy && typeof prediction.hashtag_strategy === 'string' && (
                  <p className="hashtag-strategy">{prediction.hashtag_strategy}</p>
                )}
              </div>
            </div>
          )}

          {/* Visual Tab */}
          {activeTab === 'visual' && (
            <div className="tab-content">
              <div className="content-section">
                <h3>🎨 Pauta Grafica</h3>
                <div className="visual-brief">
                  {typeof prediction.visual_brief === 'string' ? (
                    <p>{prediction.visual_brief}</p>
                  ) : (
                    <>
                      {prediction.visual_brief?.visual_concept && (
                        <div className="brief-section">
                          <h4>Concepto Visual</h4>
                          <p>{prediction.visual_brief.visual_concept}</p>
                        </div>
                      )}
                      {prediction.visual_brief?.composition && (
                        <div className="brief-section">
                          <h4>Composicion</h4>
                          <ul>
                            <li><strong>Layout:</strong> {prediction.visual_brief.composition.layout}</li>
                            <li><strong>Punto focal:</strong> {prediction.visual_brief.composition.focal_point}</li>
                          </ul>
                        </div>
                      )}
                      {prediction.visual_brief?.color_palette && (
                        <div className="brief-section">
                          <h4>Paleta de Colores</h4>
                          <div className="color-swatches">
                            {Object.entries(prediction.visual_brief.color_palette).map(([name, color]) => (
                              <div key={name} className="color-swatch">
                                <div
                                  className="swatch"
                                  style={{ backgroundColor: color }}
                                ></div>
                                <span>{name}: {color}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {prediction.visual_brief?.imagery && (
                        <div className="brief-section">
                          <h4>Imagen</h4>
                          <ul>
                            <li><strong>Sujeto:</strong> {prediction.visual_brief.imagery.main_subject}</li>
                            <li><strong>Estilo:</strong> {prediction.visual_brief.imagery.style}</li>
                            <li><strong>Mood:</strong> {prediction.visual_brief.imagery.mood}</li>
                          </ul>
                        </div>
                      )}
                      {prediction.visual_brief?.text_overlays && (
                        <div className="brief-section">
                          <h4>Textos en Imagen</h4>
                          <ul>
                            {prediction.visual_brief.text_overlays.map((overlay, i) => (
                              <li key={i}>
                                "{overlay.text}" - {overlay.position}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Reasoning Tab */}
          {activeTab === 'reasoning' && (
            <div className="tab-content">
              <div className="content-section">
                <h3>🧠 Por que esta recomendacion</h3>
                <div className="reasoning-card">
                  <p>{prediction.reasoning}</p>
                </div>
              </div>

              {prediction.content_type_reasoning && (
                <div className="content-section">
                  <h3>📊 Por que {prediction.content_type}</h3>
                  <p>{prediction.content_type_reasoning}</p>
                </div>
              )}

              {prediction.trends_incorporated && prediction.trends_incorporated.length > 0 && (
                <div className="content-section">
                  <h3>📈 Tendencias Incorporadas</h3>
                  <ul className="trends-list">
                    {prediction.trends_incorporated.map((trend, i) => (
                      <li key={i}>{trend}</li>
                    ))}
                  </ul>
                </div>
              )}

              {prediction.competitor_insights && prediction.competitor_insights.length > 0 && (
                <div className="content-section">
                  <h3>🏢 Insights de Competencia</h3>
                  <ul className="insights-list">
                    {prediction.competitor_insights.map((insight, i) => (
                      <li key={i}>{insight}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column - Predictions & Actions */}
        <div className="recommendation-sidebar">
          {/* Timing Card */}
          <div className="sidebar-card timing-card">
            <h3>📅 Cuando Publicar</h3>
            <div className="timing-info">
              <div className="timing-row">
                <span className="timing-label">Fecha:</span>
                <span className="timing-value">
                  {prediction.recommended_day_name}, {prediction.recommended_date}
                </span>
              </div>
              <div className="timing-row">
                <span className="timing-label">Hora:</span>
                <span className="timing-value">{prediction.recommended_time}</span>
              </div>
            </div>
          </div>

          {/* Predictions Card */}
          <div className="sidebar-card predictions-card">
            <h3>🎯 Performance Esperado</h3>

            <div className="prediction-metric main-metric">
              <div className="metric-value">
                {prediction.predicted_engagement_rate?.toFixed(2)}%
              </div>
              <div className="metric-label">Engagement Rate</div>
              {prediction.prediction_ranges?.er_range && (
                <div className="metric-range">
                  Rango: {prediction.prediction_ranges.er_range[0]?.toFixed(2)}% -
                  {prediction.prediction_ranges.er_range[1]?.toFixed(2)}%
                </div>
              )}
            </div>

            <div className="predictions-grid">
              <div className="prediction-metric">
                <div className="metric-icon">👁️</div>
                <div className="metric-value">{prediction.predicted_reach?.toLocaleString()}</div>
                <div className="metric-label">Alcance</div>
              </div>
              <div className="prediction-metric">
                <div className="metric-icon">❤️</div>
                <div className="metric-value">{prediction.predicted_likes?.toLocaleString()}</div>
                <div className="metric-label">Likes</div>
              </div>
              <div className="prediction-metric">
                <div className="metric-icon">💬</div>
                <div className="metric-value">{prediction.predicted_comments?.toLocaleString()}</div>
                <div className="metric-label">Comentarios</div>
              </div>
              <div className="prediction-metric">
                <div className="metric-icon">🔖</div>
                <div className="metric-value">{prediction.predicted_saves?.toLocaleString() || '-'}</div>
                <div className="metric-label">Guardados</div>
              </div>
            </div>

            <div className="confidence-bar">
              <div className="confidence-label">
                Confianza: {prediction.confidence_score}%
              </div>
              <div className="confidence-track">
                <div
                  className="confidence-fill"
                  style={{ width: `${prediction.confidence_score}%` }}
                ></div>
              </div>
            </div>

            {prediction.data_sources_used?.prediction_method && (
              <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                <span style={{ color: '#64748b' }}>Metodo:</span>
                <span style={{
                  padding: '3px 10px', borderRadius: '12px', fontWeight: 500, fontSize: '12px',
                  background: prediction.data_sources_used.prediction_method === 'ml_model' ? '#eef2ff' : '#f1f5f9',
                  color: prediction.data_sources_used.prediction_method === 'ml_model' ? '#4f46e5' : '#64748b',
                  border: `1px solid ${prediction.data_sources_used.prediction_method === 'ml_model' ? '#c7d2fe' : '#e2e8f0'}`,
                }}>
                  {prediction.data_sources_used.prediction_method === 'ml_model'
                    ? `Modelo ML ${prediction.data_sources_used.model_version || ''}`
                    : 'Promedio Historico'
                  }
                </span>
              </div>
            )}
          </div>

          {/* Actions Card */}
          {(prediction.status === 'generated' || prediction.status === 'pending_review') && (
            <div className="sidebar-card actions-card">
              <h3>⚡ Acciones</h3>

              <button
                className="action-btn approve-btn"
                onClick={() => setShowScheduleModal(true)}
                disabled={approving}
              >
                ✓ Aprobar y Programar
              </button>

              <div className="reject-section">
                <textarea
                  placeholder="Feedback (opcional) - explica por que rechazas esta recomendacion..."
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={3}
                />
                <button
                  className="action-btn reject-btn"
                  onClick={handleReject}
                  disabled={approving}
                >
                  ✗ Rechazar
                </button>
              </div>
            </div>
          )}

          {/* Status Card for approved/scheduled */}
          {(prediction.status === 'approved' || prediction.status === 'scheduled') && (
            <div className="sidebar-card status-card">
              <h3>✓ Estado</h3>
              <p className="status-message">
                Esta recomendacion ha sido aprobada.
                {prediction.scheduled_for && (
                  <span> Programada para: {new Date(prediction.scheduled_for).toLocaleString()}</span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>📅 Programar Publicacion</h3>
            <p>Selecciona cuando quieres publicar este contenido:</p>

            <div className="form-group">
              <label>Fecha y hora (opcional)</label>
              <input
                type="datetime-local"
                value={scheduleDateTime}
                onChange={(e) => setScheduleDateTime(e.target.value)}
              />
              <p className="helper-text">
                Recomendado: {prediction.recommended_day_name} {prediction.recommended_date} a las {prediction.recommended_time}
              </p>
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowScheduleModal(false)}
              >
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleApprove}
                disabled={approving}
              >
                {approving ? 'Guardando...' : 'Aprobar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CamDoBrainRecommendation;
